/**
 * BEGIN CUSTOM EDIT: AJAX Product Switching
 * Date: 2025-01-24
 * What: Seamless product color switching without page reload using AJAX + Section Rendering API
 * Why: Improve UX by avoiding page reloads while maintaining separate products for SEO
 */

class ProductVariationSwitcher {
  constructor() {
    this.cache = new Map();
    this.cacheAccessOrder = []; // Track access order for LRU (Least Recently Used)
    this.cacheMaxSize = 90; // 30 products × 3 entries each (data, section, announcement)
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes TTL to prevent stale data on long sessions
    this.isTransitioning = false;
    this.currentHandle = this.getCurrentProductHandle();
    this.abortController = null; // For cancelling in-flight requests
    // BEGIN CUSTOM EDIT: setTimeout Cleanup | Date: 2025-10-19 | What: Store timeout IDs for cleanup to prevent memory leaks | Updated: 2025-01-21 - Add preloadingHandles as instance variable to prevent unbounded closure growth
    this.preloadTimeouts = new Map(); // Track preload timeouts for cleanup
    this.preloadingHandles = new Set(); // Track currently preloading handles (instance variable for proper cleanup)
    // END CUSTOM EDIT

    // Preload nearby products on hover
    this.setupHoverPreload();

    // BEGIN CUSTOM EDIT: Beforeunload Cleanup | Date: 2025-10-19 | What: Clean up resources when user navigates away
    window.addEventListener('beforeunload', () => this.cleanup(), { once: true });
    // END CUSTOM EDIT
  }

  /**
   * Get current product handle from URL
   */
  getCurrentProductHandle() {
    const pathParts = window.location.pathname.split('/');
    const productIndex = pathParts.indexOf('products');
    return productIndex !== -1 ? pathParts[productIndex + 1] : null;
  }

  /**
   * BEGIN CUSTOM EDIT: LRU Cache Management | Date: 2025-10-19 | What: LRU eviction + TTL expiration | Updated: 2025-11-27 - Add 5min TTL to prevent stale data
   * Manage cache size using LRU (Least Recently Used) eviction strategy
   */
  manageCacheSize() {
    if (this.cache.size > this.cacheMaxSize) {
      // Remove oldest 20% of entries when limit exceeded
      const toRemove = Math.ceil(this.cacheMaxSize * 0.2);

      for (let i = 0; i < toRemove && this.cacheAccessOrder.length > 0; i++) {
        const oldestKey = this.cacheAccessOrder.shift();
        this.cache.delete(oldestKey);
      }

      console.log(`LRU cache: Removed ${toRemove} oldest entries. Current size: ${this.cache.size}`);
    }
  }

  /**
   * Get from cache and update access order
   * Returns null if entry is expired (older than cacheTTL)
   */
  getCached(key) {
    if (!this.cache.has(key)) return null;

    const entry = this.cache.get(key);

    // Check TTL - return null if expired
    if (Date.now() - entry.timestamp > this.cacheTTL) {
      this.cache.delete(key);
      const index = this.cacheAccessOrder.indexOf(key);
      if (index > -1) this.cacheAccessOrder.splice(index, 1);
      return null;
    }

    // Move to end of access order (most recently used)
    const index = this.cacheAccessOrder.indexOf(key);
    if (index > -1) {
      this.cacheAccessOrder.splice(index, 1);
    }
    this.cacheAccessOrder.push(key);

    return entry.value;
  }

  /**
   * Set in cache with timestamp and update access order
   */
  setCached(key, value) {
    this.cache.set(key, { value, timestamp: Date.now() });

    // Add to end of access order (most recently used)
    const index = this.cacheAccessOrder.indexOf(key);
    if (index > -1) {
      this.cacheAccessOrder.splice(index, 1);
    }
    this.cacheAccessOrder.push(key);

    // Check if cache size exceeded
    this.manageCacheSize();
  }
  // END CUSTOM EDIT

  /**
   * Fetch product data via Ajax Product API
   */
  async fetchProductData(handle, signal = null) {
    // Check cache first using LRU
    const cacheKey = `data-${handle}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const options = signal ? { signal } : {};
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`, options);
      if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);

      const data = await response.json();
      this.setCached(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching product data:', error);
      throw error;
    }
  }

  /**
   * Fetch rendered product section via Section Rendering API
   */
  async fetchProductSection(handle, signal = null) {
    // Check cache first using LRU
    const cacheKey = `section-${handle}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const sectionId = document.querySelector('.shopify-section--main-product')?.id?.replace('shopify-section-', '') || 'main-product';
      const options = signal ? { signal } : {};
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}?section_id=${sectionId}`, options);

      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);

      const html = await response.text();
      this.setCached(cacheKey, html);
      return html;
    } catch (error) {
      console.error('Error fetching product section:', error);
      throw error;
    }
  }

  /**
   * Fetch announcement bar section for product-specific styling
   */
  async fetchAnnouncementBarSection(handle, signal = null) {
    // Check cache first using LRU
    const cacheKey = `announcement-${handle}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const announcementBar = document.querySelector('.shopify-section--announcement-bar');
      if (!announcementBar) return null;

      const sectionId = announcementBar.id?.replace('shopify-section-', '');
      if (!sectionId) return null;

      const options = signal ? { signal } : {};
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}?section_id=${sectionId}`, options);
      if (!response.ok) return null;

      const html = await response.text();
      this.setCached(cacheKey, html);
      return html;
    } catch (error) {
      console.error('Error fetching announcement bar section:', error);
      return null;
    }
  }


  /**
   * BEGIN CUSTOM EDIT: Parse Breadcrumbs | Date: 2025-10-18 | What: Include breadcrumbs in parsed section
   * Extract and parse section HTML
   */
  parseSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    return {
      doc: doc, // Store the full document for searching anywhere
      gallery: doc.querySelector('.product-gallery'),
      info: doc.querySelector('.product-info'),
      breadcrumbs: doc.querySelector('.product-breadcrumbs-desktop'),
      title: doc.querySelector('.product-info__title'),
      price: doc.querySelector('.product-info__price'),
      variantPicker: doc.querySelector('.variant-picker'),
      productForm: doc.querySelector('form[action*="/cart/add"]'),
      badges: doc.querySelector('.product-info__badge-list'),
      sku: doc.querySelector('.product-info__sku'),
      inventory: doc.querySelector('.product-info__inventory')
    };
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Shopify Event System Integration | Date: 2025-10-19 | What: Replace form first, then use Shopify's product:rerender event for gallery updates
   * Update DOM with new product content using Shopify's Section Rendering API pattern
   */
  updateDOM(productData, sectionHTML, announcementBarHTML = null) {
    const parsed = this.parseSection(sectionHTML);

    // 1. Replace the form element FIRST (form ID changes between products)
    // This ensures the new form exists before gallery tries to find it
    const currentForm = document.querySelector('form[action*="/cart/add"]');
    const newForm = parsed.productForm;
    if (currentForm && newForm) {
      currentForm.replaceWith(newForm.cloneNode(true));
    }

    // 2. Replace the gallery (new gallery's form attribute will match the form we just replaced)
    const currentGallery = document.querySelector('.product-gallery');
    if (currentGallery && parsed.gallery) {
      currentGallery.replaceWith(parsed.gallery.cloneNode(true));
    }

    // 3. Update breadcrumbs
    const currentBreadcrumbs = document.querySelector('.product-breadcrumbs-desktop');
    if (currentBreadcrumbs && parsed.breadcrumbs) {
      currentBreadcrumbs.innerHTML = parsed.breadcrumbs.innerHTML;
    }

    // 4. Update all product info blocks using block-level rendering
    // This automatically handles: title, price, badges, SKU, inventory, text blocks, etc.
    const currentBlocks = document.querySelectorAll('.product-info [data-block-id]');
    currentBlocks.forEach(currentBlock => {
      const blockId = currentBlock.dataset.blockId;
      const blockType = currentBlock.dataset.blockType;
      if (!blockId) return;

      // Skip product-variations and associated-products - updated separately to preserve state
      if (blockType === 'product-variations' || blockType === 'associated-products') {
        return;
      }

      // Find matching block in new HTML
      const newBlock = parsed.info.querySelector(`[data-block-id="${blockId}"]`);
      if (newBlock) {
        // BEGIN CUSTOM EDIT: Custom Element Lifecycle | Date: 2025-10-20 | What: Use replaceWith() for variant-picker and execute inline scripts
        // Special handling for variant-picker to trigger disconnectedCallback/connectedCallback
        if (blockType === 'variant-picker') {
          const clonedBlock = newBlock.cloneNode(true);
          currentBlock.replaceWith(clonedBlock);

          // Execute inline scripts in the new variant-picker element
          // Scripts don't auto-execute when using replaceWith(), so we need to manually execute them
          // Use clonedBlock directly since it's now in the DOM
          const scripts = clonedBlock.querySelectorAll('script:not([data-variant])');
          scripts.forEach(oldScript => {
            const newScript = document.createElement('script');
            newScript.textContent = oldScript.textContent;
            Array.from(oldScript.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });
        } else {
          // Replace innerHTML for other blocks to preserve Shopify's rendering
          currentBlock.innerHTML = newBlock.innerHTML;
        }
        // END CUSTOM EDIT
      }
    });

    // 5. Update product-variations blocks (preserve open state)
    const allVariationBlocks = document.querySelectorAll('[data-block-type="product-variations"]');
    allVariationBlocks.forEach(currentBlock => {
      const blockId = currentBlock.dataset.blockId;
      if (!blockId) return;
      this.updateProductVariationsSelection(currentBlock, parsed.doc, blockId);
    });

    // 6. Update complementary products (skeleton → re-fetch instead of empty flash)
    this.updateComplementaryProducts(productData);

    // 7. Update announcement bar CSS if present (for product-specific metafield styling)
    if (announcementBarHTML) {
      this.updateAnnouncementBar(announcementBarHTML);
    }

    // BEGIN CUSTOM EDIT: Rebind product-rerender to new form | Date: 2026-04-27 | What: Update wrapper id+observe-form so option-info refreshes after AJAX product switch
    // The <product-rerender> wrapper persists across AJAX product switches but its
    // observe-form attribute still points at the old (now removed) form, so
    // selectCombination's product:rerender event lands on a dead listener.
    // Sync attributes from the new HTML and reconnect.
    this.rebindProductRerender(parsed.doc);
    // END CUSTOM EDIT

    // 8. Re-initialize web components (gallery, variant-picker, etc.)
    // Now that form exists, components can find it via document.forms[formId]
    this.reinitializeComponents();

    // 9. Trigger custom event for other components to react
    document.dispatchEvent(new CustomEvent('product:switched', {
      detail: { product: productData, handle: productData.handle }
    }));
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Update Product Variations Selection | Date: 2025-10-18 | What: Update only selected state without replacing entire block
   * Update product-variations block selection state without replacing DOM
   */
  updateProductVariationsSelection(currentBlock, newDocument, blockId) {
    const newBlock = newDocument.querySelector(`[data-block-id="${blockId}"]`);
    if (!newBlock) {
      console.error('Product variation block not found:', blockId);
      return;
    }

    // 1. Update the inline swatch display (color name and swatch next to "Color:")
    const currentInlineDisplay = currentBlock.querySelector('.variant-picker__option-info');
    const newInlineDisplay = newBlock.querySelector('.variant-picker__option-info');

    if (currentInlineDisplay && newInlineDisplay) {
      // Get the toggle component before updating innerHTML
      const toggle = currentBlock.querySelector('product-variations-toggle');

      // Check if toggle is currently expanded before updating
      const wasExpanded = toggle?.toggleButton?.getAttribute('aria-expanded') === 'true';

      // Disconnect the old button's event listener
      if (toggle && toggle.disconnectedCallback && typeof toggle.disconnectedCallback === 'function') {
        toggle.disconnectedCallback();
      }

      // Update the HTML (replaces the button)
      currentInlineDisplay.innerHTML = newInlineDisplay.innerHTML;

      // Re-initialize the toggle button with the new HTML
      if (toggle && toggle.connectedCallback && typeof toggle.connectedCallback === 'function') {
        toggle.connectedCallback();

        // Restore the expanded state if it was open
        if (wasExpanded && toggle._showVariations && typeof toggle._showVariations === 'function') {
          toggle._showVariations();
        }
      }
    }

    // 2. Update swatch list - check if we need full replacement or selective update
    const currentSwatches = currentBlock.querySelectorAll('[data-product-handle]');
    const newSwatches = newBlock.querySelectorAll('[data-product-handle]');

    // Check if the product handles match (same products, just different selection)
    // vs different products entirely (need full replacement)
    const currentHandles = new Set(Array.from(currentSwatches).map(s => s.dataset.productHandle));
    const newHandles = new Set(Array.from(newSwatches).map(s => s.dataset.productHandle));

    // Check if handles overlap - if they do, it's the same product list (selective update)
    // If they don't overlap, it's different products (full replacement)
    let hasOverlap = false;
    for (const handle of newHandles) {
      if (currentHandles.has(handle)) {
        hasOverlap = true;
        break;
      }
    }

    if (!hasOverlap && currentSwatches.length > 0 && newSwatches.length > 0) {
      // Different products - replace entire swatch list
      const currentContent = currentBlock.querySelector('.product-variations-toggle__content');
      const newContent = newBlock.querySelector('.product-variations-toggle__content');
      if (currentContent && newContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
    } else {
      // Same products - selective update of selected state
      // Create a map of new swatch states by handle
      const newSwatchStates = new Map();
      newSwatches.forEach(newSwatch => {
        const handle = newSwatch.dataset.productHandle;
        const link = newSwatch.querySelector('a');
        const isSelected = link?.classList.contains('is-selected');
        newSwatchStates.set(handle, isSelected);
      });

      // Update current swatches to match new states
      currentSwatches.forEach(currentSwatch => {
        const handle = currentSwatch.dataset.productHandle;
        const link = currentSwatch.querySelector('a');
        if (!link) return;

        const shouldBeSelected = newSwatchStates.get(handle);
        if (shouldBeSelected) {
          link.classList.add('is-selected');
        } else {
          link.classList.remove('is-selected');
        }
      });
    }
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Complementary Products Skeleton | Date: 2026-03-24 | What: Show skeleton while re-fetching complementary products on variant switch
   * Update complementary products with skeleton placeholder instead of empty flash
   */
  updateComplementaryProducts(productData) {
    const recEl = document.querySelector('product-recommendations[intent="complementary"]');
    if (!recEl) return;

    // Show 2 skeleton cards (matches the 2-up carousel view)
    const skeletonCount = 2;

    // Build skeleton via safe DOM methods
    const wrapper = document.createElement('div');
    wrapper.className = 'product-info__complementary-products v-stack gap-3 sm:gap-4';

    // Preserve title row if present
    const existingTitle = recEl.querySelector('.h-stack.justify-between');
    if (existingTitle) {
      wrapper.appendChild(existingTitle.cloneNode(true));
    }

    // Simple grid skeleton — no carousel complexity needed for loading state
    const grid = document.createElement('div');
    grid.className = 'complementary-skeleton';

    for (let i = 0; i < skeletonCount; i++) {
      const card = document.createElement('div');
      card.className = 'complementary-skeleton__card';

      const imgSkel = document.createElement('div');
      imgSkel.className = 'complementary-skeleton__img skeleton';

      const textWrap = document.createElement('div');
      textWrap.className = 'complementary-skeleton__text';
      const line1 = document.createElement('div');
      line1.className = 'skeleton skeleton--text';
      line1.style.width = `${70 - i * 10}%`;
      const line2 = document.createElement('div');
      line2.className = 'skeleton skeleton--text';
      line2.style.width = `${45 + i * 5}%`;
      const line3 = document.createElement('div');
      line3.className = 'skeleton skeleton--text';
      line3.style.width = '30%';
      textWrap.appendChild(line1);
      textWrap.appendChild(line2);
      textWrap.appendChild(line3);

      card.appendChild(imgSkel);
      card.appendChild(textWrap);
      grid.appendChild(card);
    }

    wrapper.appendChild(grid);
    recEl.replaceChildren(wrapper);

    // Update product attribute and trigger re-fetch
    recEl.setAttribute('product', productData.id);
    recEl._isLoaded = false;
    recEl._loadRecommendations();
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * Update announcement bar CSS for product-specific metafield styling
   */
  updateAnnouncementBar(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newStyle = doc.querySelector('style');
    const currentStyle = document.querySelector('.shopify-section--announcement-bar style');

    if (newStyle && currentStyle) {
      currentStyle.innerHTML = newStyle.innerHTML;
    }
  }

  /**
   * BEGIN CUSTOM EDIT: Re-initialize Gallery | Date: 2025-10-19 | What: Re-initialize product-gallery to restore event listeners after AJAX replacement
   * Re-initialize web components after DOM updates
   */
  reinitializeComponents() {
    // Re-initialize variant picker components
    const variantPickers = document.querySelectorAll('variant-picker');
    variantPickers.forEach(picker => {
      // Trigger connectedCallback if available
      if (picker.connectedCallback && typeof picker.connectedCallback === 'function') {
        picker.connectedCallback();
      }
    });

    // Re-initialize product gallery to restore event listeners for variant changes
    // Clean up first, then re-initialize to avoid duplicate listeners
    const productGalleries = document.querySelectorAll('product-gallery');
    productGalleries.forEach(gallery => {
      // Clean up existing state
      if (gallery.disconnectedCallback && typeof gallery.disconnectedCallback === 'function') {
        gallery.disconnectedCallback();
      }
      // Re-initialize with fresh state
      if (gallery.connectedCallback && typeof gallery.connectedCallback === 'function') {
        gallery.connectedCallback();
      }
    });

    // Dispatch event to notify other components
    document.dispatchEvent(new CustomEvent('product:blocks-updated'));
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Rebind product-rerender wrapper | Date: 2026-04-27
   * What: After AJAX product switch, the <product-rerender> wrapper element
   * persists with stale id and observe-form attributes referencing the previous
   * product's form. Sync attributes from the new HTML and force the custom
   * element to disconnect/reconnect so its listener binds to the new form.
   */
  rebindProductRerender(newDocument) {
    // Pair each existing <product-rerender> with its counterpart in the new HTML
    // by matching the section-id suffix (template--XYZ__main), which is stable
    // across products. The product-id portion differs per product.
    const sectionSuffixOf = (id) => {
      if (!id) return null;
      const match = id.match(/template--[a-z0-9_]+/i);
      return match ? match[0] : null;
    };
    const roleOf = (id) => {
      if (!id) return 'main';
      if (id.endsWith('-sticky-bar')) return 'sticky';
      return 'main';
    };

    document.querySelectorAll('product-rerender').forEach(currentRerender => {
      const currentId = currentRerender.getAttribute('id');
      const currentObserve = currentRerender.getAttribute('observe-form');
      const sectionSuffix = sectionSuffixOf(currentId) || sectionSuffixOf(currentObserve);
      const role = roleOf(currentId);
      if (!sectionSuffix) return;

      const newCandidates = Array.from(newDocument.querySelectorAll('product-rerender'))
        .filter(el => sectionSuffixOf(el.getAttribute('id')) === sectionSuffix)
        .filter(el => roleOf(el.getAttribute('id')) === role);
      const newRerender = newCandidates[0];
      if (!newRerender) return;

      const newId = newRerender.getAttribute('id');
      const newObserve = newRerender.getAttribute('observe-form');
      if (newId === currentId && newObserve === currentObserve) return;

      if (typeof currentRerender.disconnectedCallback === 'function') {
        currentRerender.disconnectedCallback();
      }
      if (newId) currentRerender.setAttribute('id', newId);
      if (newObserve) currentRerender.setAttribute('observe-form', newObserve);
      if (typeof currentRerender.connectedCallback === 'function') {
        currentRerender.connectedCallback();
      }
    });
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Update document title | Date: 2025-10-20 | What: Update browser tab title after AJAX product switch
   * Update browser URL and document title without reload
   */
  updateURL(productData, handle, updateHistory = true) {
    const newURL = `${window.Shopify.routes.root}products/${handle}`;

    // Update document title to match new product
    if (productData && productData.title) {
      // Extract store name from current title (everything after last '|')
      const currentTitle = document.title;
      const storeName = currentTitle.includes('|')
        ? currentTitle.substring(currentTitle.lastIndexOf('|')).trim()
        : '| Senkels';
      document.title = `${productData.title} ${storeName}`;
    }

    if (updateHistory) {
      window.history.pushState(
        { productHandle: handle, ajaxSwitch: true },
        '',
        newURL
      );
    }
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Instant Product Switching | Date: 2025-10-18 | What: Instant switching with no transitions for preloaded content | Updated: 2025-10-19 - Added pulsing animation for non-preloaded products (mobile) that runs in parallel with fetch
   * Main switch product function - instant when preloaded, pulsing animation when not
   */
  // BEGIN CUSTOM EDIT: Swatch Loading Pulse | Date: 2026-03-24 | What: Pulse only the clicked swatch instead of entire gallery/info
  async switchProduct(handle, updateHistory = true, clickedSwatch = null) {
    // Prevent switching if already transitioning or same product
    if (this.isTransitioning || handle === this.currentHandle) {
      return;
    }

    this.isTransitioning = true;

    // Cancel previous requests when user switches products rapidly
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Check if content is already preloaded (cached)
    const isPreloaded = this.getCached(`data-${handle}`) !== null &&
                        this.getCached(`section-${handle}`) !== null;

    try {
      // If not preloaded, pulse only the clicked swatch (not the entire page)
      if (!isPreloaded && clickedSwatch) {
        clickedSwatch.classList.add('product-switching--loading');
      }
  // END CUSTOM EDIT

      // Fetch product data, section, and announcement bar in parallel (no waiting for animation)
      const signal = this.abortController.signal;
      const [productData, sectionHTML, announcementBarHTML] = await Promise.all([
        this.fetchProductData(handle, signal),
        this.fetchProductSection(handle, signal),
        this.fetchAnnouncementBarSection(handle, signal)
      ]);

      // BEGIN CUSTOM EDIT: View Transition for Variant Switch | Date: 2026-03-24 | What: Smooth crossfade via View Transitions API when switching variants
      const canAnimate = typeof document.startViewTransition === 'function'
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Remove swatch pulse before DOM update
      if (!isPreloaded && clickedSwatch) {
        clickedSwatch.classList.remove('product-switching--loading');
      }

      if (canAnimate) {
        // Scope view-transition-names to product switch (avoid conflicts with theme toggle)
        document.documentElement.classList.add('product-switching--vt');

        const transition = document.startViewTransition(async () => {
          this.updateDOM(productData, sectionHTML, announcementBarHTML);

          // Wait for the main product image to decode before the browser captures
          // the "new" screenshot — prevents empty gallery during the wipe on mobile
          const mainImg = document.querySelector('.product-gallery__media:not([hidden]) img');
          if (mainImg) {
            await Promise.race([
              mainImg.decode().catch(() => {}),
              new Promise(resolve => setTimeout(resolve, 350))
            ]);
          }
        });

        transition.finished.finally(() => {
          document.documentElement.classList.remove('product-switching--vt');
        });
      } else {
        // No View Transitions API or reduced motion — instant update
        this.updateDOM(productData, sectionHTML, announcementBarHTML);
      }
      // END CUSTOM EDIT

      // Update URL and document title
      this.updateURL(productData, handle, updateHistory);

      // Update current handle
      this.currentHandle = handle;

      // BEGIN CUSTOM EDIT: Sync ShopifyAnalytics.meta | Date: 2026-02-02 | What: Update ShopifyAnalytics.meta.product BEFORE analytics.publish to fix stale shopify_ZZ tracking IDs
      if (window.ShopifyAnalytics?.meta?.product) {
        const selectedVariant = productData.variants?.find(v => v.available) || productData.variants?.[0];
        window.ShopifyAnalytics.meta.product = {
          id: productData.id,
          gid: `gid://shopify/Product/${productData.id}`,
          vendor: productData.vendor || '',
          type: productData.type || '',
          handle: productData.handle,
          variants: productData.variants?.map(v => ({
            id: v.id,
            price: v.price,
            name: `${productData.title} - ${v.title}`,
            public_title: v.title,
            sku: v.sku || ''
          })) || []
        };
        // Update selected variant if meta.selectedVariant exists
        if (window.ShopifyAnalytics.meta.selectedVariant !== undefined) {
          window.ShopifyAnalytics.meta.selectedVariant = selectedVariant ? {
            id: selectedVariant.id,
            price: selectedVariant.price,
            name: `${productData.title} - ${selectedVariant.title}`,
            public_title: selectedVariant.title,
            sku: selectedVariant.sku || ''
          } : null;
        }
      }
      // END CUSTOM EDIT

      // Track analytics (fires AFTER meta is updated so Web Pixels get correct product data)
      if (window.Shopify && window.Shopify.analytics) {
        window.Shopify.analytics.publish('product_viewed', {
          productId: productData.id,
          productHandle: productData.handle
        });
      }

    } catch (error) {
      // Ignore AbortErrors (these happen when user quickly switches between products)
      if (error.name === 'AbortError') {
        console.log('Product switch request cancelled');
        return;
      }

      console.error('Error switching product:', error);
      // Remove loading class if there was an error
      if (clickedSwatch) clickedSwatch.classList.remove('product-switching--loading');
      // Fallback to normal navigation
      window.location.href = `${window.Shopify.routes.root}products/${handle}`;
    } finally {
      this.isTransitioning = false;
    }
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Instant Hover Preload | Date: 2025-10-18 | What: Instant preloading on mouseover for fastest response | Updated: 2025-11-27 - Uses mouseover (bubbles) instead of mouseenter (doesn't bubble)
   * Setup hover preloading for instant switching
   * NOTE: Listener intentionally NOT removable - persists for fast navigation performance
   */
  setupHoverPreload() {
    document.addEventListener('mouseover', (e) => {
      // Ensure target is an element (not a text node) before using closest
      if (!e.target || !e.target.closest) return;

      const swatch = e.target.closest('[data-ajax-switch="true"]');
      if (!swatch) return;

      const handle = swatch.dataset.productHandle;
      if (!handle || handle === this.currentHandle) return;

      // Skip if already preloading or cached
      if (this.preloadingHandles.has(handle)) return;
      if (this.getCached(`data-${handle}`) !== null &&
          this.getCached(`section-${handle}`) !== null) return;

      // Mark as preloading and start immediately (no debounce)
      this.preloadingHandles.add(handle);

      // Preload in background - start all requests in parallel
      Promise.all([
        this.fetchProductData(handle).catch(() => {}),
        this.fetchProductSection(handle).catch(() => {}),
        this.fetchAnnouncementBarSection(handle).catch(() => {})
      ]).finally(() => {
        // Remove from preloading set immediately after completion
        // Cache check above will prevent unnecessary re-preloading
        this.preloadingHandles.delete(handle);
      });
    }, true); // Capture phase catches event early during propagation
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Cleanup Method | Date: 2025-10-19 | What: Clear timeouts and abort requests | Updated: 2025-11-27
   * Cleanup method to clear pending timeouts and abort in-flight requests
   * NOTE: Document/window listeners (mouseenter, popstate) intentionally NOT removed for fast switching
   */
  cleanup() {
    // Clear all preload timeouts (no longer used but kept for backwards compatibility)
    if (this.preloadTimeouts) {
      this.preloadTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      this.preloadTimeouts.clear();
    }

    // Clear preloading handles set
    if (this.preloadingHandles) {
      this.preloadingHandles.clear();
    }

    // Abort any in-flight requests
    if (this.abortController) {
      this.abortController.abort();
    }
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * Handle browser back/forward buttons
   * NOTE: Listener intentionally NOT removable - persists for fast navigation performance
   */
  setupHistoryNavigation() {
    window.addEventListener('popstate', (event) => {
      if (event.state?.ajaxSwitch && event.state?.productHandle) {
        this.switchProduct(event.state.productHandle, false);
      }
    });
  }
}

// Initialize the switcher
if (typeof window !== 'undefined' && window.Shopify) {
  window.productVariationSwitcher = new ProductVariationSwitcher();
  window.productVariationSwitcher.setupHistoryNavigation();
}

/**
 * END CUSTOM EDIT
 */
